import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

const ALLOWED_RESOURCE_PROTOCOLS = new Set(['http:', 'https:']);
const DISALLOWED_URL_SCHEME_PATTERN = /^(javascript|data|vbscript):/i;

export interface ITrustedResourceUrlOptions {
  basePath: string;
  queryParams?: Record<string, string>;
  allowedOrigins: string[];
  baseOrigin?: string;
}

export function createTrustedResourceUrl(
  sanitizer: DomSanitizer,
  options: ITrustedResourceUrlOptions,
): SafeResourceUrl | null {
  const { basePath, queryParams = {}, allowedOrigins, baseOrigin } = options;
  const trimmedBasePath = basePath?.trim();

  if (!trimmedBasePath || DISALLOWED_URL_SCHEME_PATTERN.test(trimmedBasePath)) {
    return null;
  }

  const resolveOrigin =
    baseOrigin ?? (typeof window !== 'undefined' ? window.location.origin : undefined);
  if (!resolveOrigin) {
    return null;
  }

  let resourceUrl: URL;
  try {
    resourceUrl = new URL(trimmedBasePath, resolveOrigin);
  } catch {
    return null;
  }

  if (!ALLOWED_RESOURCE_PROTOCOLS.has(resourceUrl.protocol)) {
    return null;
  }

  if (allowedOrigins.length > 0 && !allowedOrigins.includes(resourceUrl.origin)) {
    return null;
  }

  const searchParams = new URLSearchParams();
  Object.entries(queryParams).forEach(([key, value]) => {
    if (key) {
      searchParams.set(key, value ?? '');
    }
  });
  resourceUrl.search = searchParams.toString();

  if (allowedOrigins.length > 0 && !allowedOrigins.includes(resourceUrl.origin)) {
    return null;
  }

  return sanitizer.bypassSecurityTrustResourceUrl(resourceUrl.href);
}
